import { Category, PrimaryAccount } from '../balance/types';
import {
  ReportSheetCategoriesWithTotal,
  ReportSheetGroupsWithStats,
  ReportSheetPrimariesWithStats,
  ReportSheet,
  Total,
  ReportSheetAccountType,
} from '../types';

interface FormReportProps {
  categories: Record<string, ReportSheetCategoriesWithTotal>;
  groups: Record<string, ReportSheetGroupsWithStats>;
  primaries: Record<string, ReportSheetPrimariesWithStats>;
  accountCategories: Category[];
}

const formReport = ({
  categories,
  groups,
  primaries,
  accountCategories,
}: FormReportProps) => {
  const categoriesArr = Object.values(categories);
  const groupsArr = Object.values(groups);
  const primariesArr = Object.values(primaries);

  const nextCategories: {
    id: string;
    total: Total;
    name: string;
    active: boolean;
    number: string;
    type: ReportSheetAccountType;
  }[] = [];

  const nextGroups: {
    id: string;
    description: string;
    active: boolean;
    number: string;
    categoryId: string;
    total: Total;
  }[] = [];

  const nextPrimaries: {
    id: string;
    description: string;
    active: boolean;
    number: string;
    groupId: string;
    total: Total;
  }[] = [];

  categoriesArr?.forEach((item) => {
    if (!nextCategories?.find((category) => category.id === item.id)) {
      const currentCategory = accountCategories?.find(
        (accountCategory) => accountCategory.id === item.id,
      );

      nextCategories.push({
        ...item,
        name: currentCategory?.name as string,
        active: currentCategory?.active as boolean,
        number: currentCategory?.number as string,
        type: currentCategory?.type as ReportSheetAccountType,
      });
    }
  });

  groupsArr?.forEach((item) => {
    if (!nextGroups?.find((group) => group.id === item.id)) {
      let categoryId = '';
      const currentCategoryGroup: {
        description?: string;
        active?: boolean;
        number?: string;
      } = {};

      accountCategories?.forEach((accountCategory) => {
        const currentGroup = accountCategory.accountGroups?.find(
          (accountGroup) => accountGroup.id === item.id,
        );

        if (currentGroup) {
          categoryId = accountCategory.id;

          currentCategoryGroup.description = currentGroup.description;
          currentCategoryGroup.active = currentGroup.active;
          currentCategoryGroup.number = currentGroup.number?.toString();
        }
      });

      nextGroups.push({
        ...item,
        description: currentCategoryGroup?.description as string,
        active: currentCategoryGroup?.active as boolean,
        number: currentCategoryGroup?.number as string,
        categoryId,
      });
    }
  });

  primariesArr?.forEach((item) => {
    if (!nextPrimaries?.find((primary) => primary.id === item.id)) {
      let groupId = '';
      const currentPrimaryAccount: {
        description?: string;
        active?: boolean;
        number?: string;
      } = {};

      accountCategories?.forEach((accountCategory) => {
        accountCategory?.accountGroups?.forEach((accountGroup) => {
          const currentPrimary = accountGroup?.primaryAccounts?.find(
            (primary) => primary.id === item.id,
          ) as PrimaryAccount;

          if (currentPrimary) {
            groupId = accountGroup.id;

            currentPrimaryAccount.description = currentPrimary?.description;
            currentPrimaryAccount.active = currentPrimary?.active;
            currentPrimaryAccount.number = currentPrimary?.number;
          }
        });
      });

      nextPrimaries.push({
        ...item,
        description: currentPrimaryAccount?.description as string,
        active: currentPrimaryAccount?.active as boolean,
        number: currentPrimaryAccount?.number as string,
        groupId,
      });
    }
  });

  const lists = {
    categories: nextCategories
      ?.sort((a, b) => Number(a?.number) - Number(b?.number))
      ?.map((item) => ({
        name: item.name,
        value: item.id,
      })),
    groups: nextGroups.map((item) => ({
      name: item.description,
      value: item.id,
    })),
    primaries: nextPrimaries.map((item) => ({
      name: item.description,
      value: item.id,
    })),
  };

  const sheet: ReportSheet[] = [];

  const primariesWithGroupId: Record<string, ReportSheetPrimariesWithStats[]> = {};

  const groupWithCategoryId: Record<string, ReportSheetGroupsWithStats[]> = {};

  nextPrimaries.forEach((primary) => {
    if (!primariesWithGroupId[primary.groupId]) {
      primariesWithGroupId[primary.groupId] = [primary];
    } else {
      primariesWithGroupId[primary.groupId] = [
        ...primariesWithGroupId[primary.groupId],
        primary,
      ];
    }
  });

  nextGroups.forEach((group) => {
    const sortedPrimaries = primariesWithGroupId[group.id]?.sort(
      (a, b) => Number(a?.number) - Number(b?.number),
    );

    if (!groupWithCategoryId[group.categoryId]) {
      groupWithCategoryId[group.categoryId] = [
        { ...group, primaries: sortedPrimaries },
      ];
    } else {
      groupWithCategoryId[group.categoryId] = [
        ...groupWithCategoryId[group.categoryId],
        { ...group, primaries: sortedPrimaries },
      ];
    }
  });

  nextCategories.forEach((category) => {
    sheet.push({
      ...category,
      groups: groupWithCategoryId[category.id]?.sort(
        (a, b) => Number(a?.number) - Number(b?.number),
      ),
    });
  });

  const reportResult = {
    lists,
    sheet,
  };

  return reportResult;
};

export default formReport;
